/** Matches {@link ROLE_EXP_PER_REP} in roleProgressService — kept local to avoid Firebase in tests. */
const DEFAULT_ROLE_EXP_PER_REP = 10;

/**
 * @typedef {object} StudySessionRoleExpRow
 * @property {string} roleId
 * @property {string} [roleTitle]
 * @property {string} [roleIconClass]
 * @property {number} expAmount
 * @property {number} level
 * @property {number} current_exp
 * @property {number} exp_to_level
 * @property {boolean} leveledUp
 */

/**
 * @typedef {object} StudySessionSummaryPayload
 * @property {number} correctCount
 * @property {number} totalAttempted
 * @property {StudySessionRoleExpRow[]} roleExpUpdates
 */

/**
 * Merge role EXP rows from a single rep into the session accumulator.
 * @param {Map<string, StudySessionRoleExpRow>} byRoleId
 * @param {Array<Partial<StudySessionRoleExpRow>>|null|undefined} updates
 */
export function mergeRoleExpUpdatesIntoSession(byRoleId, updates) {
  if (!byRoleId || !Array.isArray(updates)) return;

  for (const update of updates) {
    const roleId = update?.roleId != null ? String(update.roleId).trim() : '';
    if (!roleId) continue;

    const expGain = Number.isFinite(update.expAmount)
      ? update.expAmount
      : DEFAULT_ROLE_EXP_PER_REP;
    const existing = byRoleId.get(roleId);

    if (!existing) {
      byRoleId.set(roleId, {
        roleId,
        roleTitle: update.roleTitle || roleId,
        roleIconClass: update.roleIconClass || 'ti-user',
        expAmount: expGain,
        level: update.level ?? 1,
        current_exp: update.current_exp ?? 0,
        exp_to_level: update.exp_to_level ?? 1,
        leveledUp: Boolean(update.leveledUp)
      });
      continue;
    }

    byRoleId.set(roleId, {
      ...existing,
      roleTitle: update.roleTitle || existing.roleTitle,
      roleIconClass: update.roleIconClass || existing.roleIconClass,
      expAmount: existing.expAmount + expGain,
      level: update.level ?? existing.level,
      current_exp: update.current_exp ?? existing.current_exp,
      exp_to_level: update.exp_to_level ?? existing.exp_to_level,
      leveledUp: existing.leveledUp || Boolean(update.leveledUp)
    });
  }
}

/**
 * @param {object} input
 * @param {number} [input.correctCount]
 * @param {number} [input.totalAttempted]
 * @param {Map<string, StudySessionRoleExpRow>} [input.roleExpByRole]
 * @returns {StudySessionSummaryPayload}
 */
export function buildStudySessionSummaryPayload({
  correctCount = 0,
  totalAttempted = 0,
  roleExpByRole = new Map()
}) {
  return {
    correctCount: Math.max(0, Math.round(correctCount)),
    totalAttempted: Math.max(0, Math.round(totalAttempted)),
    roleExpUpdates: [...roleExpByRole.values()]
  };
}

/**
 * @param {StudySessionSummaryPayload|null|undefined} payload
 * @returns {boolean}
 */
export function shouldShowRegisteredSessionSummary(payload) {
  return (payload?.correctCount ?? 0) > 0;
}
